import { useState, useEffect, lazy, Suspense } from 'react';
import Dashboard from './dashboard';
import LandingPage from './LandingPage';
import { isNative as isCapacitorNative } from './utils/platform';
import { initNativeOnSignIn, teardownNative } from './utils/nativeInit';
import { NativeSplash } from './dashboard/components/NativeSplash';
import UpdateAvailablePill from './components/UpdateAvailablePill';
import { LegalPage } from './pages/LegalPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ResetHandoff from './pages/ResetHandoff';
import { PublicPreview } from './dashboard/masterclass/PublicPreview';
import { RIKKI_MODULE, RIKKI_MODULE_KEY } from './dashboard/masterclass/rikkiModule';
import { NBA_PLAYER_DEV_MODULE, NBA_PLAYER_DEV_KEY } from './dashboard/masterclass/nbaPlayerDevModule';
import { NBA_CLA_LAYERS_MODULE, NBA_CLA_LAYERS_KEY } from './dashboard/masterclass/nbaClaLayersModule';
import { WARMUP_MODULE, WARMUP_MODULE_KEY } from './dashboard/masterclass/warmupModule';
import { POINT_GUARD_MASTERCLASS, POINT_GUARD_MASTERCLASS_KEY } from './dashboard/masterclass/pointGuardModule';
import { BookCallPage } from './dashboard/pages/BookCallPage';
import { BuyCertPage } from './dashboard/pages/BuyCertPage';
import { CertSalesPage } from './dashboard/pages/CertSalesPage';
import { CertPathPage } from './dashboard/pages/CertPathPage';
import { captureAdSource } from './utils/adSource';
import { AdminProvider, useAdmin } from './dashboard/shared/AdminContext';
import { supabase } from './supabaseClient';
import { captureRefFromUrl } from './dashboard/utils/affiliateRef';
import { syncMetaPixelToRoute } from './utils/metaPixel';
import { IS_STAGING } from "./config/environment";

// Capture an affiliate ?ref=<code> on first load so it survives to signup +
// checkout (attribution-first; see dashboard/utils/affiliateRef.js).
captureRefFromUrl();
// Meta Pixel: web only, marketing and sign-up pages only (PQ-12), never for
// opted-out visitors. Follows the hash as the visitor moves in or out.
syncMetaPixelToRoute();
if (typeof window !== 'undefined') window.addEventListener('hashchange', syncMetaPixelToRoute);
// Note which ad (utm_*) brought the visitor, before the hash changes and the
// parameters are lost. The cert checkout carries it to Stripe (adSource.js).
captureAdSource();

// Password-recovery safety net. Supabase's recovery link lands at
// `/?recovery=1#access_token=…&type=recovery` (our intended redirect) OR, if
// that redirect URL isn't on Supabase's allowlist, at the bare Site URL
// `/#access_token=…&type=recovery`. In the second case `?recovery=1` is gone
// and the client's detectSessionInUrl would just sign the user in on the
// platform with no password prompt — exactly the bug coaches hit. Capture
// `type=recovery` from the URL synchronously at module load, BEFORE the client
// parses and clears the hash, so we can still force the reset screen.
// An org invite (admin Organizations, api/admin-actions resolveOrInvite)
// takes the same road: its link is ?recovery=1&invite=1, or type=invite when
// the redirect falls back to the Site URL. Either way the invited coach sets
// a password before anything else, or they'd be signed in with none.
const LOADED_WITH_RECOVERY = typeof window !== "undefined" && (
  (window.location.hash || "").includes("type=recovery") ||
  (window.location.hash || "").includes("type=invite") ||
  new URLSearchParams(window.location.search).get("recovery") === "1"
);

// Admin surfaces are lazy-loaded so coaches never download the legacy
// admin portal (which pulls in recharts, ~0.9MB) or the standalone admin
// review pages on boot — they're reachable only from admin-only routes.
// Each render site below is wrapped in a Suspense boundary using the
// AdminAuthLoading full-screen fallback defined just under these imports.
const AdminPortal = lazy(() => import('./bam-admin'));
// A game page shared by link (/g/<token>): public, no login (2026-10-24).
const AFSShowcase = lazy(() => import('./dashboard/film/AFSShowcase'));
const GAME_LINK = /^\/g\/(demo|[A-Za-z0-9_-]{24,64})\/?$/;
// A player's own page (2026-10-24): /p/<token>, no login.
const AFSPlayerPage = lazy(() => import('./dashboard/film/AFSPlayerPage'));
const PLAYER_LINK = /^\/p\/([A-Za-z0-9_-]{24,64})\/?$/;
// Advantage Flow on its own (2026-10-24): its page at /afs, and starting a plan at #/afs-start. Web only.
const AFSLanding = lazy(() => import('./dashboard/film/AFSLanding'));
const AFSStart = lazy(() => import('./dashboard/film/AFSStart'));
// The tagger and interest applications (2026-10-25): public, no account, web only.
const AFSApply = lazy(() => import('./dashboard/film/AFSApplyScreen'));
// The coaches platform's internship application (2026-10-29): #/apply/intern, its own page. Public, web only.
const InternApply = lazy(() => import('./dashboard/interns/InternApplyScreen'));
// The Modern Basketball Blueprint's resources (2026-10-26): the book's QR code lands on #/blueprint. Web only.
const BlueprintPage = lazy(() => import('./dashboard/blueprint/BlueprintPage'));
// The free drop (2026-11-02): last week's drop from a personal email link, #/free-drop/<token>, 7 days. Web only.
const FreeDropPage = lazy(() => import('./dashboard/drops/FreeDropPage'));
const PendingUploadsPage = lazy(() => import('./dashboard/admin/PendingUploadsPage'));
const DrillTagReviewPage = lazy(() => import('./dashboard/admin/DrillTagReviewPage'));
const AdminHubStandalone = lazy(() => import('./dashboard/admin/AdminHubPage').then(m => ({ default: m.AdminHubStandalone })));
// BAM IQ's local preview (scripts/bamiq/previewServer.mjs): only in a build
// made with REACT_APP_BAMIQ_PREVIEW=1, which is never the deployed one.
const BamIQPreview = process.env.REACT_APP_BAMIQ_PREVIEW === '1'
  ? lazy(() => import('./dashboard/bamiq/PreviewApp'))
  : null;

// Tiny full-screen loader so the standalone admin pages don't flash
// the "Admins only" gate while the is_admin lookup is in flight.
function AdminAuthLoading() {
  return (
    <div style={{
      minHeight: "100vh", background: "#0A0A0A", color: "#9CA3AF",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'DM Sans', sans-serif", fontSize: 13, letterSpacing: 0.4,
    }}>
      Loading…
    </div>
  );
}

// Standalone admin page needs its own profile fetch (Dashboard normally
// handles that). This small wrapper loads the current user's is_admin
// flag into AdminContext, then renders the review page. We delay
// rendering the page until the lookup completes so admins don't see
// a flash of "Admins only" while the profiles query is in flight.
function AdminPendingUploadsWithProfileLoader() {
  const { setIsAdmin } = useAdmin();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { if (!cancelled) setReady(true); return; }
        const { data } = await supabase.from('profiles').select('is_admin').eq('id', user.id).limit(1);
        if (!cancelled) setIsAdmin(!!data?.[0]?.is_admin);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [setIsAdmin]);
  if (!ready) return <AdminAuthLoading/>;
  return <Suspense fallback={<AdminAuthLoading/>}><PendingUploadsPage/></Suspense>;
}

// Same wrapper for the drill tag review page.
function AdminDrillReviewWithProfileLoader() {
  const { setIsAdmin } = useAdmin();
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { if (!cancelled) setReady(true); return; }
        const { data } = await supabase.from('profiles').select('is_admin').eq('id', user.id).limit(1);
        if (!cancelled) setIsAdmin(!!data?.[0]?.is_admin);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [setIsAdmin]);
  if (!ready) return <AdminAuthLoading/>;
  return <Suspense fallback={<AdminAuthLoading/>}><DrillTagReviewPage/></Suspense>;
}

// Allowlist of masterclass slugs that can be previewed without an account.
// The slug in the URL (e.g. #/free/modern-point-guard) matches the module's
// key. The Modern Point Guard masterclass is gated as a lead magnet — the
// reader's email-gate fires after chapter 1 in publicMode.
const PUBLIC_MODULES = {
  [RIKKI_MODULE_KEY]: RIKKI_MODULE,
  [WARMUP_MODULE_KEY]: WARMUP_MODULE,
  "tyler-clark": WARMUP_MODULE,
  [POINT_GUARD_MASTERCLASS_KEY]: POINT_GUARD_MASTERCLASS,
  // One chapter, so the reader's gate (publicMode && active > 0) never
  // fires: the whole article is free and the ask is the cta at the end.
  [NBA_PLAYER_DEV_KEY]: NBA_PLAYER_DEV_MODULE,
  // Its follow-up, same deal: free, one chapter, ask at the end.
  [NBA_CLA_LAYERS_KEY]: NBA_CLA_LAYERS_MODULE,
};

function App() {
  const [route, setRoute] = useState(() => window.location.hash);
  // Auth probe so already-signed-in visitors hitting the bare domain
  // skip the marketing landing and go straight to the platform. Anyone
  // who explicitly opens #/landing still sees the marketing page.
  const [authChecked, setAuthChecked] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  // True when we arrived from a password-recovery link (or Supabase fires
  // PASSWORD_RECOVERY). Forces the Set New Password screen even when the
  // recovery redirect fell back to the Site URL and dropped ?recovery=1.
  const [recoveryMode, setRecoveryMode] = useState(LOADED_WITH_RECOVERY);

  // Capture the Early User Family Program flag on app boot. ?invited=1
  // arrives via the invite email link, lands the user on the marketing
  // landing page first, and is gone by the time the user reaches the
  // Paywall component. Persist to sessionStorage immediately so the
  // perk badge + Stripe trial/coupon get applied at checkout regardless
  // of routing.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("invited") === "1") {
        sessionStorage.setItem("bam_early_user", "1");
      }
    } catch {}
  }, []);

  useEffect(() => {
    const onHash = () => setRoute(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      const session = data?.session;
      setHasSession(!!session);
      setAuthChecked(true);
      if (session?.user?.id) initNativeOnSignIn(session.user.id);
    });
    // Keep the flag fresh if the user signs in/out in another tab or
    // signs out from the dashboard (which sets hash to "").
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      setHasSession(!!session);
      setAuthChecked(true);
      // Recovery link fired — show the reset screen no matter where the
      // redirect landed the user.
      if (event === "PASSWORD_RECOVERY") setRecoveryMode(true);
      if (event === "SIGNED_IN" && session?.user?.id) {
        initNativeOnSignIn(session.user.id);
      } else if (event === "SIGNED_OUT") {
        teardownNative();
      }
    });
    return () => { cancelled = true; sub?.subscription?.unsubscribe?.(); };
  }, []);

  const exitAdmin = () => {
    window.location.hash = '#/platform';
    setRoute('#/platform');
  };

  // Bare `#/admin` redirects to the redesigned admin dashboard. The
  // legacy AdminPortal (Inbox-style sidebar) is still accessible at
  // `#/admin-legacy` for any features that haven't been ported yet —
  // mostly Content Manager / Drill Review / AI Training surfaces. Once
  // those are fully replaced in the new admin shell, we can remove
  // bam-admin.jsx entirely.
  // Catch both `#/admin` and `#/admin/` (the trailing-slash variant is
  // what browsers sometimes auto-fill from history). Both bounce to the
  // redesigned admin dashboard.
  if (route === '#/admin' || route === '#/admin/') {
    // 7-section restructure landed the admin shell on /admin/home
    // (was /admin/dashboard, which also still redirects — but going
    // straight here saves a hop and avoids a flash of the legacy URL).
    if (typeof window !== 'undefined') window.location.hash = '#/admin/home';
    return null;
  }
  // A coach's game page, shared by link: anyone with it, no login, before any sign-in routing.
  const gameLink = typeof window !== 'undefined' ? (GAME_LINK.exec(window.location.pathname) || [])[1] : null;
  if (gameLink) return <Suspense fallback={<div style={{ minHeight: '100vh', background: '#0A0A0A' }}/>}><AFSShowcase token={gameLink}/></Suspense>;
  const playerLink = typeof window !== 'undefined' ? (PLAYER_LINK.exec(window.location.pathname) || [])[1] : null;
  if (playerLink) return <Suspense fallback={<div style={{ minHeight: '100vh', background: '#0A0A0A' }}/>}><AFSPlayerPage token={playerLink}/></Suspense>;
  if (typeof window !== 'undefined' && /^\/afs\/?$/.test(window.location.pathname)) return <Suspense fallback={<div style={{ minHeight: '100vh', background: '#0A0A0A' }}/>}><AFSLanding/></Suspense>;
  if (route === '#/admin-legacy') return <Suspense fallback={<AdminAuthLoading/>}><AdminPortal onExitAdmin={exitAdmin} /></Suspense>;
  // Password reset. Two URL shapes:
  //   • Legacy hash route: /#/reset (kept for any in-flight emails sent before
  //     the redirectTo fix). The double-hash bug means tokens may not be
  //     auto-detected; ResetPasswordPage parses them manually as a fallback.
  //   • Query-param route: /?recovery=1#access_token=… — Supabase's recovery
  //     tokens land cleanly at the start of location.hash, so its auto-detect
  //     works. This is what new resetPasswordForEmail calls produce.
  if (route === '#/reset' || route.startsWith('#/reset')) return <ResetPasswordPage />;
  // Admin-generated reset links point here, not straight at Supabase. The
  // link is single use and every chat app prefetches URLs to build a preview
  // card, so a link pasted into Instagram or Slack is spent before the person
  // taps it. This page touches nothing until they press the button.
  // Checked BEFORE ?recovery=1 because the real link is carried inside it.
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('open_reset')) return <ResetHandoff />;
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('recovery') === '1') return <ResetPasswordPage />;
  // Safety net: recovery link that fell back to the Site URL (no ?recovery=1).
  // recoveryMode is seeded from the URL at load and set by the PASSWORD_RECOVERY
  // event. onDone clears it so leaving the reset screen returns to normal routing.
  if (recoveryMode) return <ResetPasswordPage onDone={() => setRecoveryMode(false)} />;
  // Legal pages — public, accessible without auth
  if (route === '#/privacy') return <LegalPage id="privacy" />;
  if (route === '#/terms') return <LegalPage id="terms" />;
  if (route === '#/refund') return <LegalPage id="refund" />;
  if (route === '#/support') return <LegalPage id="support" />;
  if (route === '#/afs-terms') return <LegalPage id="afs-terms" />;
  if (route.startsWith('#/afs-start')) {
    if (isCapacitorNative()) { window.location.hash = '#/platform'; return null; }
    return <Suspense fallback={<div style={{ minHeight: '100vh', background: '#0A0A0A' }}/>}><AFSStart/></Suspense>;
  }
  if (route === '#/blueprint' || route.startsWith('#/blueprint/') || route.startsWith('#/blueprint?')) {
    if (isCapacitorNative()) { window.location.hash = '#/platform'; return null; }
    return <Suspense fallback={<div style={{ minHeight: '100vh', background: '#0A0A0A' }}/>}><BlueprintPage view={route.startsWith('#/blueprint/resources') ? 'resources' : 'start'}/></Suspense>;
  }
  if (route.startsWith('#/apply/')) {
    if (isCapacitorNative()) { window.location.hash = '#/platform'; return null; }
    if (/^#\/apply\/intern/.test(route)) return <Suspense fallback={<div style={{ minHeight: '100vh', background: '#0A0A0A' }}/>}><InternApply/></Suspense>;
    // The interest list said "we open it up later this week"; plans are live (2026-11-03), so it goes to the page itself.
    if (!/^#\/apply\/tagger/.test(route)) { window.location.replace(`/afs${window.location.search || ''}`); return null; }
    return <Suspense fallback={<div style={{ minHeight: '100vh', background: '#0A0A0A' }}/>}><AFSApply kind="tagger"/></Suspense>;
  }
  // The free drop: one drop, outside sign-in and the paywall, for anyone holding the link (signed out, or signed in
  // without paying). Ahead of the Dashboard so the paywall itself is untouched. Reader-app rule: unlocking paid
  // content outside in-app purchase never happens inside the iOS app, so native goes to the normal home.
  if (route.startsWith('#/free-drop/')) {
    if (isCapacitorNative()) { window.location.hash = '#/platform'; return null; }
    const token = route.slice('#/free-drop/'.length).split(/[?/]/)[0];
    return <Suspense fallback={<div style={{ minHeight: '100vh', background: '#0A0A0A' }}/>}><FreeDropPage token={token} signedIn={hasSession}/></Suspense>;
  }
  // Public / free masterclass previews — no account required.
  if (route.startsWith('#/free/')) {
    const slug = route.replace('#/free/', '').split('?')[0].split('/')[0];
    return <PublicPreview module={PUBLIC_MODULES[slug]} />;
  }
  // Public book-a-call surface. The page itself checks auth + active
  // subscription and renders the right state (paywall pitch or
  // embedded Cal widget). Coleman puts this URL on the GHL marketing
  // site's "Book an info call" CTA. Subscription-gated so unqualified
  // leads can't book a sales call without committing $34.99/mo first.
  if (route === '#/book-call' || route.startsWith('#/book-call?')) {
    // Reader-app rule: this is a sales/pricing surface. Native users never
    // see it — bounce them into the platform.
    if (isCapacitorNative()) { window.location.hash = '#/platform'; return null; }
    return <BookCallPage />;
  }
  // Public cert sales page (2026-11-02). Where paid ads and the marketing
  // site send people: the whole story plus checkout, no account needed.
  if (route === '#/certification' || route.startsWith('#/certification?')) {
    // Reader-app rule: cert pricing and outside payment never show on iOS.
    if (isCapacitorNative()) { window.location.hash = '#/platform'; return null; }
    return <CertSalesPage />;
  }
  // "Find your path": the cert sales call as a few taps. Ads can link here.
  if (route === '#/cert-path' || route.startsWith('#/cert-path?')) {
    if (isCapacitorNative()) { window.location.hash = '#/platform'; return null; }
    return <CertPathPage />;
  }
  // Direct-to-checkout cert purchase. GHL's "Purchase BAM Certification"
  // CTA points here so already-convinced visitors skip the landing
  // page and go straight to a focused checkout surface. The page
  // handles auth state internally (sign-in pitch if anon, Stripe
  // checkout if subscribed/not, "already own this" if purchased).
  if (route === '#/buy-cert' || route.startsWith('#/buy-cert?')) {
    // Reader-app rule: cert checkout / $1,399 pricing must not appear on iOS.
    if (isCapacitorNative()) { window.location.hash = '#/platform'; return null; }
    return <BuyCertPage />;
  }
  // Legacy standalone admin routes — kept only as fallbacks. The
  // primary versions live inside the redesigned admin shell:
  //   #/admin/uploads      (was #/admin/pending-uploads)
  //   #/admin/drill-review (same path; now wrapped in AdminLayout)
  // The /admin/pending-uploads URL keeps working in case anything has
  // bookmarked it externally, but the new sidebar entry points to
  // /admin/uploads which renders inside the shell.
  if (route === '#/admin/pending-uploads') {
    if (typeof window !== 'undefined') window.location.hash = '#/admin/content/uploads';
    return null;
  }
  if (route === '#/admin/hub') return <Suspense fallback={<AdminAuthLoading/>}><AdminHubStandalone/></Suspense>;
  // Explicit landing route — always show marketing, even when signed in.
  // Useful escape hatch for signed-in users who want the marketing view.
  // EXCEPTION: in the native (Capacitor) build we MUST NOT show the
  // marketing page — App Store policy forbids displaying pricing or
  // external checkout in a "reader" app. Native users go straight to
  // sign-in regardless of how they landed on /landing.
  if (route === '#/landing') {
    if (isCapacitorNative()) {
      window.location.hash = '#/signin';
      return null;
    }
    return <LandingPage />;
  }
  // Bare domain ("" / "#" / "#/") — send signed-in visitors straight to
  // the platform; native unauthenticated visitors to sign-in; web
  // unauthenticated visitors to the marketing page.
  if (route === '' || route === '#/' || route === '#') {
    if (!authChecked) return null;
    if (hasSession) {
      // Admin landing preference (Settings → Admin, per-device). Decided
      // HERE rather than after the dashboard mounts, so an admin who opted
      // in never sees a flash of the coach dashboard on the way. Yields to
      // a fresh push tap, which is the whole point of tapping it.
      //
      // Reading the flag alone is enough: the toggle that sets it only
      // renders for admins, and /admin/* is gated again on arrival.
      let wantsAdminHome = false;
      try {
        wantsAdminHome = localStorage.getItem('bam_admin_home') === '1'
          && !localStorage.getItem('bam_push_route');
      } catch { /* storage unavailable — fall through to the platform */ }
      const dest = wantsAdminHome ? '#/admin/home' : '#/platform';
      if (window.location.hash !== dest) window.location.hash = dest;
      return null;
    }
    if (isCapacitorNative()) {
      window.location.hash = '#/signin';
      return null;
    }
    return <LandingPage />;
  }
  // Everything else (platform, certified, community, etc.) → Dashboard
  // Dashboard internally handles auth + paywall gating
  return <Dashboard />;
}

// Wrapped App that always renders NativeSplash above every route. The
// splash is `position: fixed; z-index: 999999` so it covers any early
// return from App's body (including the brief `return null` window
// during auth probe + SignInScreen + Dashboard).
//
// SPLASH-LOOP FIX:
// On iOS, the WKWebView can be torn down and re-created when the app
// backgrounds (system share sheet, push permission prompt, Face ID
// prompt). Each re-creation remounts AppWithSplash with splashDone =
// false, causing the splash to animate again — coaches saw it "flash
// in a loop" when interacting with anything that triggered a system
// overlay. Persist the "splash already shown" flag to sessionStorage
// so it survives WebView re-creations within the same app session.
// sessionStorage clears on app quit, so the splash still animates on
// genuine cold starts.
const SPLASH_DONE_KEY = "bam_splash_done";
function readSplashDone() {
  if (!isCapacitorNative()) return true;
  try { return sessionStorage.getItem(SPLASH_DONE_KEY) === "1"; } catch { return false; }
}
function AppWithSplash() {
  const [splashDone, setSplashDone] = useState(readSplashDone);
  const markDone = () => {
    try { sessionStorage.setItem(SPLASH_DONE_KEY, "1"); } catch {}
    setSplashDone(true);
  };
  return (
    <>
      <App/>
      {!splashDone && <NativeSplash onDone={markDone}/>}
      {/* Proactive stale-build banner. Polls /index.html every 5min +
          on tab focus; shows a non-blocking "New version available"
          pill when the running bundle's main.*.js hash differs from
          what's deployed. Reload is user-initiated. */}
      <UpdateAvailablePill/>
      {IS_STAGING && (
        <div role="status" style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 2147483000, pointerEvents: "none",
          padding: "3px 10px", background: "#B23B3B", color: "#fff", font: "700 11px/1.4 'DM Sans', sans-serif", textAlign: "center", letterSpacing: ".06em" }}>
          STAGING: invented data only. Payments, billing, email and tracking are off.
        </div>
      )}
    </>
  );
}

export default BamIQPreview
  ? function Preview() { return <Suspense fallback={null}><BamIQPreview/></Suspense>; }
  : AppWithSplash;
